import axios from 'axios';
import { getToken } from '../../utils/auth';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api';

/** Axios client for the PMA module (`/api/pma/*`), authenticated with the regular QuillStack session token. */
export const api = axios.create({ baseURL: `${BASE_URL.replace(/\/+$/, '')}/pma` });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// The permission middleware reports denials using raw permission keys ("Missing permission(s): change_task_deadline").
// Translate that into a plain-language message once, here.
const PERMISSION_DENIED_PATTERNS: { re: RegExp; joiner: 'and' | 'or' }[] = [
  { re: /^Missing permission\(s\): (.+)$/, joiner: 'and' },
  { re: /^Requires one of: (.+)$/, joiner: 'or' },
];

function joinReadably(items: string[], joiner: 'and' | 'or'): string {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${joiner} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, ${joiner} ${items[items.length - 1]}`;
}

function humanizePermissionDenial(message: string): string | null {
  for (const { re, joiner } of PERMISSION_DENIED_PATTERNS) {
    const match = message.match(re);
    if (!match) continue;
    const actions = match[1].split(',').map((key) => key.trim().replace(/_/g, ' '));
    return `You don't have permission to ${joinReadably(actions, joiner)}. Please contact your administrator.`;
  }
  return null;
}

export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
    if (err.response?.status === 403) return humanizePermissionDenial(message) ?? message;
    return message;
  }
  return fallback;
}
